import React, { useEffect, useState } from 'react';
import { Button, Descriptions, Modal, Space, Table, Tag, Typography } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import {
	ILocationDiagram,
	IMaterialLengthRow,
	IShelfWithLocations,
	useGetLocationBarsQuery,
} from '../../../../redux/api/inventory';
import { locationCapacityLabel } from './rackVisual.helpers';
import SlotFrontView from '../../../../components/Inventory/SlotFrontView';
import { SLOT_TYPES } from '../../constants/inventory';

interface Props {
	open: boolean;
	location: ILocationDiagram | null;
	shelf?: IShelfWithLocations | null;
	canWrite: boolean;
	onEdit: () => void;
	onClose: () => void;
	onPermissionDenied: () => void;
}

/**
 * Wide modal showing the material lengths (bars) stored in a location, with a
 * shortcut to edit the location itself.
 */
const LocationDetailModal: React.FC<Props> = ({
	open,
	location,
	shelf,
	canWrite,
	onEdit,
	onClose,
	onPermissionDenied,
}) => {
	const [page, setPage] = useState(1);

	useEffect(() => {
		if (open) setPage(1);
	}, [location?.id, open]);

	const { data, isFetching } = useGetLocationBarsQuery(
		{ id: location?.id as string, page },
		{ skip: !open || !location },
	);

	const capacity = location ? locationCapacityLabel(location) : '';
	const isIndividualSlot = !!location && SLOT_TYPES.includes(location.type);

	return (
		<Modal
			title={
				location ? (
					<Space>
						<strong>Location {location.code}</strong>
						{shelf && <Tag>{shelf.name}</Tag>}
					</Space>
				) : (
					'Location'
				)
			}
			open={open}
			onCancel={onClose}
			width={1000}
			footer={[
			<Button
				key="edit"
				type="primary"
				icon={<EditOutlined />}
				onClick={canWrite ? onEdit : onPermissionDenied}
			>
				Edit Location
			</Button>,
				<Button key="close" onClick={onClose}>
					Close
				</Button>,
			]}
		>
			{location && (
				<Descriptions size="small" column={4} style={{ marginBottom: 16 }}>
					<Descriptions.Item label="Items">
						{location.number_of_items}
					</Descriptions.Item>
					<Descriptions.Item label="Occupancy">
						{Math.round(location.occupancy_percent)}%
					</Descriptions.Item>
					{capacity && (
						<Descriptions.Item label="Capacity">{capacity}</Descriptions.Item>
					)}
					{location.description && (
						<Descriptions.Item label="Description">
							{location.description}
						</Descriptions.Item>
					)}
				</Descriptions>
			)}

			{open && isIndividualSlot && location && (
				<SlotFrontView locationId={location.id} />
			)}

		<Table<IMaterialLengthRow>
			size="small"
			rowKey="id"
			loading={isFetching}
			dataSource={data?.results ?? []}
			pagination={{
				current: page,
				pageSize: 20,
				total: data?.count ?? 0,
				onChange: (p) => setPage(p),
				showSizeChanger: false,
				showTotal: (total) => `${total} items`,
				size: 'small',
			}}
			scroll={{ y: 360 }}
				locale={{ emptyText: 'No material lengths stored in this location' }}
			columns={[
				{
					title: 'Friendly ID',
					dataIndex: 'friendly_id',
					render: (friendlyId: string, row) => {
						const params = new URLSearchParams({ search: friendlyId });
						if (row.shape_id) params.set('shape', row.shape_id);
						return (
							<Typography.Link
								href={`/material/materials?${params.toString()}`}
								target="_blank"
								rel="noopener noreferrer"
							>
								{friendlyId}
							</Typography.Link>
						);
					},
				},
				{ title: 'Bundle', dataIndex: 'bundle_id', render: (v) => v || '—' },
				{ title: 'Material Type', dataIndex: 'material_type', render: (v) => v || '—' },
					{ title: 'Shape', dataIndex: 'material_shape', render: (v) => v || '—' },
					{
						title: 'Current Length (IN)',
						dataIndex: 'current_length',
						align: 'right',
					},
					{
						title: 'Weight (LBS)',
						dataIndex: 'weight',
						align: 'right',
					},
				]}
			/>
		</Modal>
	);
};

export default LocationDetailModal;
